import {IAuthenticationClient} from '@/services/IAuthenticationClient';
import {
    AxiosInstance,
    AxiosRequestConfig,
    AxiosResponse,
    CancelToken
} from 'axios';
import apiAxiosClient from '@/utils/axios';
import {AuthResponse} from '@/interfaces/authResponse';
import {LoginRequest} from '@/interfaces/loginRequest';
import {RefreshTokenRequest} from '@/interfaces/refreshTokenRequest';
import {RegisterRequest} from '@/interfaces/registerRequest';
import {RegisterResponse} from '@/interfaces/registerResponse';
import {isAxiosError, throwException} from '@/utils/errors';

export class AuthenticationClient implements IAuthenticationClient {
    protected jsonParseReviver: ((key: string, value: any) => any) | undefined =
        undefined;
    private instance: AxiosInstance;
    private baseUrl: string;

    constructor(baseUrl?: string, instance?: AxiosInstance) {
        this.instance = instance ? instance : apiAxiosClient;

        this.baseUrl = baseUrl !== undefined && baseUrl !== null ? baseUrl : '';
    }

    /**
     * @param body (optional)
     * @return Success
     */
    login(
        body?: LoginRequest | undefined,
        cancelToken?: CancelToken | undefined
    ): Promise<AuthResponse> {
        let url_ = this.baseUrl + '/api/Authentication/login';
        url_ = url_.replace(/[?&]$/, '');

        const content_ = JSON.stringify(body);

        const options_ = <AxiosRequestConfig>{
            data: content_,
            method: 'POST',
            url: url_,
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json'
            },
            cancelToken
        };

        return this.instance
            .request(options_)
            .catch((_error: any) => {
                if (isAxiosError(_error) && _error.response) {
                    return _error.response;
                } else {
                    throw _error;
                }
            })
            .then((_response: AxiosResponse) => {
                return this.processLogin(_response);
            });
    }

    /**
     * @param body (optional)
     * @return Success
     */
    refresh(
        body?: RefreshTokenRequest | undefined,
        cancelToken?: CancelToken | undefined
    ): Promise<AuthResponse> {
        let url_ = this.baseUrl + '/api/Authentication/refresh';
        url_ = url_.replace(/[?&]$/, '');

        const content_ = JSON.stringify(body);

        const options_ = <AxiosRequestConfig>{
            data: content_,
            method: 'POST',
            url: url_,
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json'
            },
            cancelToken
        };

        return this.instance
            .request(options_)
            .catch((_error: any) => {
                if (isAxiosError(_error) && _error.response) {
                    return _error.response;
                } else {
                    throw _error;
                }
            })
            .then((_response: AxiosResponse) => {
                return this.processRefresh(_response);
            });
    }

    /**
     * @param body (optional)
     * @return Success
     */
    register(
        body?: RegisterRequest | undefined,
        cancelToken?: CancelToken | undefined
    ): Promise<RegisterResponse> {
        let url_ = this.baseUrl + '/api/Authentication/register';
        url_ = url_.replace(/[?&]$/, '');

        const content_ = JSON.stringify(body);

        const options_ = <AxiosRequestConfig>{
            data: content_,
            method: 'POST',
            url: url_,
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json'
            },
            cancelToken
        };

        return this.instance
            .request(options_)
            .catch((_error: any) => {
                if (isAxiosError(_error) && _error.response) {
                    return _error.response;
                } else {
                    throw _error;
                }
            })
            .then((_response: AxiosResponse) => {
                return this.processRegister(_response);
            });
    }

    protected processLogin(response: AxiosResponse): Promise<AuthResponse> {
        const status = response.status;
        const _headers: any = {};
        if (response.headers && typeof response.headers === 'object') {
            for (const k in response.headers) {
                if (Object.prototype.hasOwnProperty.call(response.headers, k)) {
                    _headers[k] = response.headers[k];
                }
            }
        }
        if (status === 200) {
            const _responseText = response.data;
            let result200: any = null;
            const resultData200 = _responseText;
            result200 = resultData200;
            return Promise.resolve<AuthResponse>(result200);
        } else if (status === 400) {
            const _responseText = response.data;
            let result400: any = null;
            const resultData400 = _responseText;
            result400 = resultData400;
            return throwException(
                'Bad Request',
                status,
                _responseText,
                _headers,
                result400
            );
        } else if (status !== 200 && status !== 204) {
            const _responseText = response.data;
            return throwException(
                'An unexpected server error occurred.',
                status,
                _responseText,
                _headers
            );
        }
        return Promise.resolve<AuthResponse>(<any>null);
    }

    protected processRefresh(response: AxiosResponse): Promise<AuthResponse> {
        const status = response.status;
        const _headers: any = {};
        if (response.headers && typeof response.headers === 'object') {
            for (const k in response.headers) {
                if (Object.prototype.hasOwnProperty.call(response.headers, k)) {
                    _headers[k] = response.headers[k];
                }
            }
        }
        if (status === 200) {
            const _responseText = response.data;
            let result200: any = null;
            const resultData200 = _responseText;
            result200 = resultData200;
            return Promise.resolve<AuthResponse>(result200);
        } else if (status === 400) {
            const _responseText = response.data;
            let result400: any = null;
            const resultData400 = _responseText;
            result400 = resultData400;
            return throwException(
                'Bad Request',
                status,
                _responseText,
                _headers,
                result400
            );
        } else if (status !== 200 && status !== 204) {
            const _responseText = response.data;
            return throwException(
                'An unexpected server error occurred.',
                status,
                _responseText,
                _headers
            );
        }
        return Promise.resolve<AuthResponse>(<any>null);
    }

    protected processRegister(
        response: AxiosResponse
    ): Promise<RegisterResponse> {
        const status = response.status;
        const _headers: any = {};
        if (response.headers && typeof response.headers === 'object') {
            for (const k in response.headers) {
                if (Object.prototype.hasOwnProperty.call(response.headers, k)) {
                    _headers[k] = response.headers[k];
                }
            }
        }
        if (status === 200) {
            const _responseText = response.data;
            let result200: any = null;
            const resultData200 = _responseText;
            result200 = resultData200;
            return Promise.resolve<RegisterResponse>(result200);
        } else if (status === 400) {
            const _responseText = response.data;
            let result400: any = null;
            const resultData400 = _responseText;
            result400 = resultData400;
            return throwException(
                'Bad Request',
                status,
                _responseText,
                _headers,
                result400
            );
        } else if (status !== 200 && status !== 204) {
            const _responseText = response.data;
            return throwException(
                'An unexpected server error occurred.',
                status,
                _responseText,
                _headers
            );
        }
        return Promise.resolve<RegisterResponse>(<any>null);
    }
}

export const AuthenticationService = new AuthenticationClient();
export default AuthenticationService;
